import type { PassCell, PassProductView, SeasonView } from "@highmarch/app";
import { BalanceData, EventReward, NO_REWARD, RolloverSummary, SeasonTheme, SeasonsData, cosmeticSkinPay } from "@highmarch/domain";
import { checkHtml, flagHtml, heroHtml, priceButtonHtml, secHtml, tagHtml, wellHtml } from "../components";
import { barHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { uiUrl } from "../portraits";
import { bannerSceneUrl } from "../shops/shopsArt";
import { fmtCountdown } from "../deals/dealsModel";
import { countdownHtml, valueBurstHtml } from "../deals/dealsTiles";
import { partWell, rewardParts, rewardText, skinPart } from "./passTiles";

/**
 * The Pass sheet's markup (MP-22) in the Deals pages' language (design system v2, the shop skin): Captain Bram's hero
 * with the season's countdown, the tier plate, the two-row reward track (free over premium: wells, a padlock on
 * premium until it is bought, the gold CLAIM on what is ready), the Premium and Premium+ cards (the honest value burst,
 * the gold price with its VIP XP bubble) and the rollover summary. Every number comes from @highmarch/app's SeasonView.
 */

export interface PassCtx {
  readonly balance: BalanceData;
  readonly level: number;
  readonly now: number;
  /** The store's own prices by product id (empty until it answers: the reference price shows). */
  readonly prices: ReadonlyMap<string, string>;
  /** Paid passes can be bought here (a store is open). */
  readonly paid: boolean;
  readonly sandbox: boolean;
  /** Whether the player has a skin already (the catalog's skin grants): an owned season skin is not paid again. */
  readonly ownsSkin: (id: string) => boolean;
}

/** The season's skin as it will pay: "" when there is none or it is already had (a later loop of the season list). */
const payingSkin = (theme: SeasonTheme, c: PassCtx): string => (theme.skin && !c.ownsSkin(theme.skin) ? theme.skin : "");
/** Gems the cosmetic tier pays instead of the season's skin when it is already had (seasons.json repeatSkinGems); 0 otherwise. */
const repeatGems = (theme: SeasonTheme, c: PassCtx): number => cosmeticSkinPay(c.balance.seasons, theme, theme.skin !== "" && c.ownsSkin(theme.skin)).gems;
/** The premium tier that pays the season's cosmetic. */
const cosmeticTier = (d: SeasonsData): number => d.premiumTrack.find((t) => t.cosmetic)?.tier ?? d.tiers;
/** "You own the Border Warden skin: tier 30 pays 300 gems instead" (plain text; "" when the skin will pay). */
export function repeatSkinLine(theme: SeasonTheme, c: PassCtx): string {
  const gems = repeatGems(theme, c), skin = skinPart(theme.skin);
  return gems > 0 && skin ? `You own the ${skin.name.replace(/ tower skin$| skin$/, "")} skin: tier ${cosmeticTier(c.balance.seasons)} pays ${gems.toLocaleString("en-US")} gems instead` : "";
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);
/** Captain Bram rallying the campaign under its standard, with the campaign horn and the laurel (tools/art/kit/heroes_bpy.py
 * `pass`): his face stays in frame. */
export const PASS_HERO_ART = "deals/page_pass.webp";
/** The War Campaign's painted icon (the standard with its laurel and the horn, tools/art/icons3d/deals.py `deals_pass`). */
export const PASS_ICON = "icons/deals_pass.png";
/** A Premium card's wells: one row beside the value burst. */
const WELLS_PER_CARD = 4;

// ------------------------------------------------------------------ hero and tier plate

export function heroBlockHtml(v: SeasonView, c: PassCtx): string {
  const timer = v.live ? countdownHtml(v.endsAt, fmtCountdown(v.endsAt - c.now), c.now, "Ends in") : countdownHtml(v.startsAt, fmtCountdown(v.startsAt - c.now), c.now, "Starts in");
  // owner 2026-10-21: the Pass is a Store page, with the Store's bright banner (no blurb: text to a minimum)
  return heroHtml({ art: "", title: esc(v.theme.name), timer, className: "dhero phero",
    bright: { theme: "sky", cast: uiUrl("captain/bram_thumbsup@3x.png"), prop: uiUrl(PASS_ICON), scene: bannerSceneUrl("pass") } });
}

export function tierPlateHtml(v: SeasonView): string {
  const frac = v.maxed ? 1 : v.into / v.need;
  const line = v.maxed ? "Every tier reached!" : `${fmtCompact(v.into)} / ${fmtCompact(v.need)} XP to tier ${v.tier + 1}`;
  const r = v.xpRules;
  return `<section class="k-card plate ptier"><div class="pbadge"><small>TIER</small><b class="disp">${v.tier}</b><small>of ${v.tiers}</small></div>
    <div class="pbarcol"><span class="pline">${line}</span>${barHtml(frac, "gold", "pbar")}
    <small class="phow">Win <b>+${r.win}</b> · Loss <b>+${r.loss}</b> · Order <b>+${r.order}</b> · Event reward <b>+${r.eventNode}</b></small></div></section>`;
}

// ------------------------------------------------------------------ the track

/**
 * One cell of the track: the reward's first part in its well — on the premium cosmetic tier the season's skin, its
 * render in a lit well — "+n" for the rest, and its state's foot.
 */
function cellHtml(cell: PassCell | null, c: PassCtx, theme: SeasonTheme): string {
  if (!cell) return `<span class="pcell none" aria-hidden="true"></span>`;
  // a claimed cell keeps its skin (the claim paid it, so it is had now); an unclaimed one names it only while it will pay
  const skin = cell.cosmetic && cell.track === "premium" ? skinPart(cell.state === "claimed" ? theme.skin : payingSkin(theme, c)) : null;
  const title = cell.cosmetic ? theme.title : "";
  const parts = [...(skin ? [skin] : []), ...rewardParts(c.balance, c.level, cell.reward)];
  const more = parts.length - 1 + (title ? 1 : 0);
  const first = parts[0];
  const well = first ? partWell(first, "sm", skin ? "pskin" : "", cell.state === "claimed") : wellHtml({ art: icon("star", 30), size: "sm", tag: "span" });
  const foot = cell.state === "ready" ? `<span class="pclaim disp">CLAIM</span>`
    : cell.state === "claimed" ? checkHtml("pcheck")
    : cell.state === "locked" ? `<span class="plock">${icon("lock", 13)}</span>` : "";
  const instead = cell.cosmetic && cell.track === "premium" && cell.state !== "claimed" ? repeatGems(theme, c) : 0;
  const words = [instead > 0 ? `${instead.toLocaleString("en-US")} gems for the skin you own` : "", rewardText(c.balance, c.level, cell.reward, title, skin ? theme.skin : "")].filter(Boolean).join(" · ");
  const label = `Tier ${cell.tier} ${cell.track}: ${words}${cell.state === "ready" ? " · claim now" : cell.state === "claimed" ? " · claimed" : cell.state === "locked" ? " · Premium" : ""}`;
  return `<button type="button" class="pcell s-${cell.state}${cell.cosmetic ? " cosm" : ""}" data-track="${cell.track}" data-tier="${cell.tier}" data-words="${esc(words)}"${cell.reward.chest !== null ? ` data-chest="${cell.reward.chest}"` : ""} aria-label="${esc(label)}">`
    + `${well}${more > 0 ? `<i class="pmore">+${more}</i>` : ""}${foot}</button>`;
}

export function trackHtml(v: SeasonView, c: PassCtx): string {
  const cols = v.rows.map((r) => `<div class="pcol${r.reached ? " on" : ""}${r.tier === v.tier ? " cur" : ""}" data-tier="${r.tier}">`
    + `${cellHtml(r.free, c, v.theme)}<i class="pnum disp">${r.tier}</i>${cellHtml(r.premium, c, v.theme)}</div>`).join("");
  const fill = v.tier <= 0 ? 0 : Math.min(1, (v.tier - 0.5) / v.tiers);
  // the section header (secHtml's look): its note gives way to "Claim all" while two or more rewards wait
  const end = v.claimable > 1 ? `<button type="button" class="btn blue pall"><span>Claim all · ${v.claimable}</span></button>`
    : `<small>${v.pass === "free" ? "Premium pays every tier" : "Every tier is yours"}</small>`;
  return `<div class="k-sec phead"><b>Rewards</b>${end}</div>
    <section class="k-card ptrack" style="--n:${v.tiers}">
      <div class="prail"><span class="plbl free disp">FREE</span><span class="plbl prem disp">PREMIUM</span></div>
      <div class="pscroll"><div class="pcols"><i class="prule"><i class="prfill" style="width:${(fill * 100).toFixed(2)}%"></i></i>${cols}</div></div>
    </section>${trackNote(v, c)}`;
}

/** Under the track, before the cosmetic tier is claimed: what it pays when the season's skin is already had. */
function trackNote(v: SeasonView, c: PassCtx): string {
  const line = repeatSkinLine(v.theme, c);
  const done = v.rows.find((r) => r.premium?.cosmetic)?.premium?.state === "claimed";
  return line && !done ? `<p class="dfoot prepeat">${esc(line)}.</p>` : "";
}

// ------------------------------------------------------------------ the purchase cards

/** What Premium pays over the season, summed: gems, essence, sigils, speed-up time, chests by rarity. */
export function premiumTotals(d: SeasonsData): { gems: number; essence: number; sigils: number; minutes: number; legendary: number; chests: number } {
  const t = { gems: 0, essence: 0, sigils: 0, minutes: 0, legendary: 0, chests: 0 };
  for (const { reward: r } of d.premiumTrack) {
    t.gems += r.gems; t.essence += r.essence; t.minutes += r.speedUpMinutes;
    if (r.sigil !== null) t.sigils += Math.max(1, r.sigils);
    if (r.chest !== null) { t.chests++; if (r.chest >= 4) t.legendary++; }
  }
  return t;
}

const sum = (part: Partial<EventReward>): EventReward => ({ ...NO_REWARD, ...part });

function totalsWells(c: PassCtx, d: SeasonsData, theme: SeasonTheme): string[] {
  const t = premiumTotals(d);
  const well = (r: EventReward) => rewardParts(c.balance, c.level, r).map((p) => partWell(p)).join("");
  const skin = skinPart(payingSkin(theme, c));
  // one row of four: the season's skin, the gems, the Legendary chest, the essence, the speed-up time (sigils are named in the list)
  return [skin ? partWell(skin, "sm", "pskin") : "", well(sum({ gems: t.gems })), t.legendary > 0 ? well(sum({ chest: 4 })) : "", well(sum({ essence: t.essence })), t.minutes > 0 ? well(sum({ speedUpMinutes: t.minutes })) : ""].filter(Boolean);
}

/** The card's cosmetic line: the season's skin (when it has one) and the pass's title. */
function cosmeticWords(theme: SeasonTheme, plus: boolean, c: PassCtx): string {
  const skin = skinPart(payingSkin(theme, c)), gems = repeatGems(theme, c), owned = skinPart(theme.skin);
  const title = `the title <b>“${esc(plus ? theme.plusTitle : theme.title)}”</b>`;
  if (skin) return `The <b>${esc(skin.name.replace(/ skin$/, ""))}</b> skin and ${title}`;
  // a later loop of the season list: the skin is had, so its tier pays gems (said before buying)
  if (gems > 0 && owned) return `<b>${gems.toLocaleString("en-US")} gems</b> in place of the <b>${esc(owned.name.replace(/ skin$/, ""))}</b> skin you own, and ${title}`;
  return title.charAt(0).toUpperCase() + title.slice(1);
}

function productCardHtml(p: PassProductView, v: SeasonView, c: PassCtx, d: SeasonsData): string {
  const plus = p.level === "plus";
  const price = c.prices.get(p.product) ?? p.priceLabel;
  const t = premiumTotals(d);
  const lines = [
    plus ? `<li><b>+${p.tierSkip} tiers</b> at once</li>` : "",
    `<li>All <b>${v.tiers}</b> premium rewards: <b>${t.gems.toLocaleString("en-US")}</b> gems, a Legendary chest, essence, ${t.sigils} sigils and speed-ups</li>`,
    `<li>${cosmeticWords(v.theme, plus, c)}</li>`,
  ].filter(Boolean).join("");
  const skip = plus ? wellHtml({ art: `<b class="pskip disp">+${p.tierSkip}</b>`, size: "sm", tag: "span", cap: "tiers" }) : "";
  const wells = [skip, ...totalsWells(c, d, v.theme)].filter(Boolean).slice(0, WELLS_PER_CARD).join("");
  const pay = p.blocked
    ? `<span class="k-stamp dbuy pbuy blocked">${p.blocked === "already owned" ? "Owned" : "Not on sale"}</span>`
    : priceButtonHtml({ currency: "real", amount: esc(price), vipXp: p.vipPoints, bubble: "left", className: "dbuy pbuy p-card", data: `data-product="${esc(p.product)}"` });
  return `<article class="k-card hl doffer poffer ${plus ? "plus" : "prem"}" data-level="${p.level}">${flagHtml(plus ? "Premium+" : "Premium")}${p.showValue ? valueBurstHtml(p.valuePercent) : ""}
    <div class="dwells">${wells}</div>
    <div class="drow"><ul class="plist">${lines}</ul><div class="dpay">${pay}</div></div></article>`;
}

export function offersHtml(v: SeasonView, c: PassCtx, d: SeasonsData): string {
  if (!v.live) return "";
  if (v.pass !== "free") {
    const t = premiumTotals(d);
    return `<section class="k-card plate powned">${checkHtml("pok")}<div><b class="disp">${v.pass === "plus" ? "Premium+" : "Premium"} is yours</b>
      <small>Every tier's premium reward this season</small></div></section>`;
  }
  if (!c.paid) return `<p class="dfoot">Premium comes with the iPhone app.</p>`;
  return `${secHtml("Go Premium", "This season only")}${productCardHtml(v.premium, v, c, d)}${productCardHtml(v.plus, v, c, d)}`;
}

// ------------------------------------------------------------------ the rollover summary

export function summaryHtml(s: RolloverSummary, c: PassCtx, d: SeasonsData, seasonName: string): string {
  const wells = s.rewards.map((r) => {
    const def = (r.track === "free" ? d.free : d.premiumTrack).find((x) => x.tier === r.tier);
    const first = def ? rewardParts(c.balance, c.level, def.reward)[0] : undefined;
    return first ? partWell(first) : "";
  }).join("");
  const n = s.rewards.length;
  const body = n > 0 ? `Tier <b>${s.tier}</b> reached. The <b>${n}</b> reward${n === 1 ? "" : "s"} you left behind ${n === 1 ? "is" : "are"} yours:` : `Tier <b>${s.tier}</b> reached, every reward claimed. Well fought!`;
  return `<section class="k-card hl psummary">${flagHtml(`Season ${s.season + 1} complete`)}<p class="psum">${body}</p>
    ${n > 0 ? `<div class="dwells">${wells}</div>` : ""}<p class="psub">${esc(seasonName)} has begun. A fresh track, from tier 0.</p>
    <button type="button" class="btn blue pseen"><span>GOT IT</span></button></section>`;
}

export function footHtml(c: PassCtx, d: SeasonsData): string {
  const lines = [`A season runs ${d.daysPerSeason} days`, "Unclaimed rewards come to you when it ends",
    c.paid && c.sandbox ? "Test build: nothing is charged" : ""].filter(Boolean);
  return `<p class="dfoot">${lines.map(esc).join(" · ")}</p>`;
}
